import {
  Select,
  DateInput,
  TextArea,
  Input,
  Icon,
  Stack,
  useTheme,
  Title
} from "@introist/react-foundation/v2";
import { useState } from "react";
import { FormField, uploadFormFile } from "services/api/PublicFormApi";
import { StoredFileInput } from "../../../../foundation/blocks/files/StoredFileInput";
import { asFileIdentifier } from "../../../../services/api/FileApiV2";
import { Radio } from "../../../../components/atoms/Radio/Radio";
import parseHtml from "html-react-parser";
import styled from "styled-components";

const getLuminance = (hex: string) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const toLinear = (v: number) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

const ScaleCircleRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const ScaleCircle = styled.div<{ $selected: boolean; $error: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid
    ${p =>
      p.$error
        ? "var(--palette-danger-default)"
        : p.$selected
          ? "var(--palette-primary-default)"
          : "var(--palette-border-default)"};
  background: ${p => (p.$selected ? "var(--palette-primary-default)" : "transparent")};
  color: ${p => (p.$selected ? "#fff" : "var(--palette-foreground-default)")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--typography-size-p);
  font-family: inherit;
  cursor: pointer;
  user-select: none;
  flex-shrink: 0;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: ${p =>
      p.$error ? "var(--palette-danger-default)" : "var(--palette-primary-default)"};
    background: ${p =>
      p.$selected ? "var(--palette-primary-default)" : "var(--palette-primary-ghosted)"};
  }
`;

const ScaleLabel = styled(Title)<{ $visible: boolean }>`
  display: block;
  min-height: 1.4em;
  opacity: ${p => (p.$visible ? 1 : 0)};
  transition: opacity 0.15s ease;
`;

const ScaleOptions = ({
  options,
  value,
  onChange,
  error,
  primaryColor
}: {
  options: string[];
  value?: string;
  onChange: (v: string) => void;
  error: boolean;
  primaryColor?: string;
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const selectedIndex = options.indexOf(value ?? "");

  const activeIndex = hoveredIndex !== null ? hoveredIndex : selectedIndex;
  const activeLabel = activeIndex >= 0 ? options[activeIndex] : null;

  const color = primaryColor && !error ? primaryColor : null;
  const textColor = color ? (getLuminance(color) > 0.5 ? "#000" : "#fff") : "#fff";

  return (
    <Stack vertical gap="small">
      <ScaleLabel $visible={activeLabel !== null}>{activeLabel ?? " "}</ScaleLabel>
      <ScaleCircleRow>
        {options.map((option, i) => {
          const selected = i === selectedIndex;
          const circleStyle = color
            ? {
                borderColor: selected ? color : `${color}60`,
                background: selected ? color : "transparent",
                color: selected ? textColor : "var(--palette-foreground-default)"
              }
            : undefined;
          return (
            <ScaleCircle
              key={i}
              $selected={selected}
              $error={error}
              style={circleStyle}
              onClick={() => onChange(option)}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {i + 1}
            </ScaleCircle>
          );
        })}
      </ScaleCircleRow>
    </Stack>
  );
};

const RadioOptionRow = styled.div<{ $error: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid
    ${p => (p.$error ? "var(--palette-danger-default)" : "var(--palette-border-default)")};
  border-radius: var(--rounding-medium);
  cursor: pointer;
  user-select: none;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover {
    border-color: ${p =>
      p.$error ? "var(--palette-danger-default)" : "var(--palette-primary-default)"};
    background: var(--palette-primary-ghosted);
  }
`;

const RadioOptionLabel = styled(Title)`
  flex: 1;
  min-width: 0;
`;

const RadioOptions = ({
  options,
  value,
  onChange,
  error
}: {
  options: string[];
  value?: string;
  onChange: (v: string) => void;
  error: boolean;
}) => (
  <Stack vertical gap="xSmall">
    {options.map(option => (
      <RadioOptionRow key={option} $error={error} onClick={() => onChange(option)}>
        <Radio checked={value === option} readOnly />
        <RadioOptionLabel>{option}</RadioOptionLabel>
      </RadioOptionRow>
    ))}
  </Stack>
);

export const HtmlDescription = styled(Title)`
  // Paragraphs - remove default margins, add small spacing between
  & > p,
  & p {
    margin: 0;
    margin-bottom: var(--spacing-xSmall);
  }

  & > p:last-child,
  & p:last-child {
    margin-bottom: 0;
  }

  // Images - now inline within paragraphs, inherit alignment from parent
  & img {
    max-width: 100%;
    height: auto;
    display: inline-block;
    vertical-align: middle;
    border-radius: var(--rounding-small);
  }

  // Links - improve readability and touch targets
  & a {
    word-break: break-word;
    text-decoration: underline;
  }

  // Lists - better spacing
  & ul,
  & ol {
    margin: var(--spacing-small) 0;
    padding-left: var(--spacing-large);
  }

  & li {
    margin-bottom: var(--spacing-xSmall);
  }

  & li:last-child {
    margin-bottom: 0;
  }

  // Headings - ensure proper sizing
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin: var(--spacing-small) 0;
    max-width: 100%;
  }

  // Tables - prevent overflow
  & table {
    max-width: 100%;
    overflow-x: auto;
    display: block;
  }

  // Code blocks - prevent overflow
  & pre,
  & code {
    max-width: 100%;
    overflow-x: auto;
    word-break: break-word;
  }

  // Generic - prevent any element from overflowing
  & * {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

export const Field = ({
  field,
  value,
  onChange,
  error,
  responseId,
  previewMode = false,
  primaryColor
}: {
  field: FormField;
  value?: string;
  onChange: (value?: any) => any;
  error?: string;
  responseId?: string;
  previewMode?: boolean;
  primaryColor?: string;
}) => {
  const { theme } = useTheme();
  const renderInput = () => {
    if (field.type === "scale") {
      return (
        <ScaleOptions
          options={field.options ?? []}
          value={value}
          onChange={onChange}
          error={!!error}
          primaryColor={primaryColor}
        />
      );
    }

    if (field.type === "select") {
      if (field.display === "radio") {
        return (
          <RadioOptions
            options={field.options ?? []}
            value={value}
            onChange={onChange}
            error={!!error}
          />
        );
      }

      const selectOptions = (field.options ?? []).map(o => ({ key: o }));

      return (
        <Select
          options={selectOptions}
          size="small"
          style={{ width: "100%" }}
          value={value}
          onSelect={opt => onChange(opt.key)}
          error={!!error}
        />
      );
    }

    if (field.type === "date") {
      return (
        <DateInput
          onChange={onChange}
          size="small"
          value={value}
          error={!!error}
          displayFormat="DD.MM.YYYY"
        />
      );
    }

    if (field.type === "longtext") {
      return <TextArea value={value} onChange={onChange} error={!!error} />;
    }

    if (field.type === "number") {
      return (
        <Input
          type="number"
          onChange={onChange}
          value={value}
          size="small"
          endAdornment={<Icon name="urgencyMid" dimmed />}
          error={!!error}
        />
      );
    }

    if (field.type === "email") {
      return (
        <Input
          type="email"
          onChange={onChange}
          value={value}
          size="small"
          endAdornment={<Icon name="atTheRate" dimmed />}
          error={!!error}
        />
      );
    }

    if (field.type === "file") {
      return (
        <StoredFileInput
          onChange={file => onChange(asFileIdentifier(file))}
          value={value}
          size="small"
          endAdornment={<Icon name="file" dimmed />}
          error={!!error}
          uploadHandler={
            responseId ? (file, progress) => uploadFormFile(responseId, file, progress) : undefined
          }
        />
      );
    }

    return (
      <Input
        size="small"
        onChange={onChange}
        value={value}
        endAdornment={<Icon name="description" dimmed />}
        error={!!error}
      />
    );
  };

  // the field description can be plain text or HTML
  // if it's HTML, we need to parse it
  // if it is not, we can just render it as is
  const descriptionIsHtml = /<\/?[a-z][\s\S]*>/i.test(field.description || "");
  const descriptionContent = descriptionIsHtml
    ? parseHtml(field.description || "")
    : field.description;

  return (
    <Stack vertical gap="small">
      <Stack vertical gap="xSmall">
        <Stack gap="small">
          <Title variant="bold">
            {field.title}
            {field.required && <span> *</span>}
          </Title>
          <Stack gap="small" style={{ marginLeft: "auto" }}>
            {error && <Icon name="warning" color={theme.palette.danger.default}></Icon>}
            <Title color={theme.palette.danger.default}>{error}</Title>
          </Stack>
        </Stack>

        {field.description && <HtmlDescription>{descriptionContent}</HtmlDescription>}
      </Stack>

      {renderInput()}
    </Stack>
  );
};
